import common from '../common';
import commonGame from '../commonGame';

const _removeItems = Symbol('_removeItems');
const _findItems = Symbol('_findItems');
const _createItems = Symbol('_createItems');
const _findItemCount = Symbol('_findItemCount');
const _updateNavControls = Symbol('_updateNavControls');
const _onPageIndex_Changed = Symbol('_onPageIndex_Changed');
const _onNextButton_Click = Symbol('_onNextButton_Click');
const _onPrevButton_Click = Symbol('_onPrevButton_Click');
const _onMenuButton_Click = Symbol('_onMenuButton_Click');

export default class PalettePanel extends PIXI.Container {
	constructor() {
		super();

		// Constants.
		this.ITEMS_PER_ROW = 5;
		this.ITEMS_PER_PAGE = 15;
		this.PALETTE_TOOLS = common.gameConfig.palettePanel.tools;
		this.PALETTE_TYPE_TO_TOOL = [
			this.PALETTE_TOOLS.pencil,
			this.PALETTE_TOOLS.brush,
			this.PALETTE_TOOLS.stamp,
			this.PALETTE_TOOLS.fill,
			this.PALETTE_TOOLS.ink,
			this.PALETTE_TOOLS.spray,
			this.PALETTE_TOOLS.fill
		];

		// Attributes.
		this._bgGradientSprite = null;
		this._panel = null;
		this._boxSprite = null;
		this._type = null;
		this._nextBtn = null;
		this._prevBtn = null;
		this._pageIndex = 0;
		this._pageCount = 0;
		this._arrBtnInfo = [];

		// Signals.
		this.signals = {};
		this.signals.onColourChanged = new P3.utils.MiniSignal();
		this.signals.onStampChanged = new P3.utils.MiniSignal();

		this._tweens = new P3.utils.TWEEN.Group();
	}

	init() {
		// Bg gradient sprite.
		this._bgGradientSprite = new PIXI.Sprite(PIXI.Texture.WHITE);
		this._bgGradientSprite.tint = 0x000000;
		this._bgGradientSprite.alpha = 0.6;
		this._bgGradientSprite.anchor = new PIXI.Point(0.5, 0.5);
		this._bgGradientSprite.scale.x = common.STAGE_WIDTH * 1.25;
		this._bgGradientSprite.scale.y = common.STAGE_HEIGHT;
		this._bgGradientSprite.interactive = true;
		this._bgGradientSprite.click = this._bgGradientSprite.touchend = this.hide.bind(this);
		this.addChild(this._bgGradientSprite);

		// Panel.
		this._panel = new PIXI.Container();
		this.addChild(this._panel);

		// Box sprite.
		this._boxSprite = new PIXI.Sprite(P3.assets.texture('popup_box'));
		this._boxSprite.interactive = true;
		this._boxSprite.anchor = new PIXI.Point(0.5, 0.5);
		// this._boxSprite.width = 712;
		// this._boxSprite.height = 512;
		this._panel.addChild(this._boxSprite);

		// Next button.
		var btnStatesAux = new P3.ButtonStates(
			P3.assets.texture('btn_arrow_out'),
			P3.assets.texture('btn_arrow_over'),
			P3.assets.texture('btn_arrow_down')
		);
		this._nextBtn = new P3.Button(btnStatesAux);
		this._nextBtn.anchor = new PIXI.Point(0.5, 0.5);
		this._nextBtn.scale = new PIXI.Point(1.5, 1.5);
		this._nextBtn.position.x = this._boxSprite.width * 0.5 + 50;
		this._nextBtn.alpha = 0;
		this._nextBtn.interactive = false;
		this._nextBtn.signals.click.add(this[_onNextButton_Click], this);
		this._panel.addChild(this._nextBtn);

		// Prev button.
		btnStatesAux = new P3.ButtonStates(
			P3.assets.texture('btn_arrow_out'),
			P3.assets.texture('btn_arrow_over'),
			P3.assets.texture('btn_arrow_down')
		);
		this._prevBtn = new P3.Button(btnStatesAux);
		this._prevBtn.anchor = new PIXI.Point(0.5, 0.5);
		this._prevBtn.scale = new PIXI.Point(-1.5, 1.5);
		this._prevBtn.position.x = -this._boxSprite.width * 0.5 - 50;
		this._prevBtn.alpha = 0;
		this._prevBtn.interactive = false;
		this._prevBtn.signals.click.add(this[_onPrevButton_Click], this);
		this._panel.addChild(this._prevBtn);

		this.visible = false;
	}

	show(toolType) {
		console.log('palette show');
		this._type = toolType;

		this._arrBtnInfo = this[_findItems](this._type);

		this._pageIndex = 0;
		this[_updateNavControls]();

		this[_createItems](this._arrBtnInfo);

		// Animate-in.
		this.visible = true;
		this.alpha = 0;
		this._panel.y = -50;

		new P3.utils.TWEEN.Tween(this._panel, this.tweens)
		    .to({
		        y: 0
		    }, 250)
		    .easing(P3.utils.TWEEN.Easing.Quadratic.Out)
		    .start();

		new P3.utils.TWEEN.Tween(this, this.tweens)
			.to({
				alpha: 1
			}, 250)
			.easing(P3.utils.TWEEN.Easing.Quadratic.Out)
			.start();

		// Sfx.
		P3.audio.playSound('sfx_palette_select_00');
	}

	hide() {
		new P3.utils.TWEEN.Tween(this._panel, this.tweens)
			.to({
				y: 50
			}, 250)
			.easing(P3.utils.TWEEN.Easing.Quadratic.Out)
			.start();

		new P3.utils.TWEEN.Tween(this, this.tweens)
			.to({
				alpha: 0
			}, 250)
			.onComplete(()=> {
				this.visible = false;
				this._boxSprite.removeChildren();
				common.game.scene.addTutorialEvent(commonGame.TUT_EVENT.PALETTE_PANEL_HIDDEN);
			})
			.easing(P3.utils.TWEEN.Easing.Quadratic.Out)
			.start();

		P3.audio.playSound('sfx_palette_close_00');
	}

	destroy(options) {
		this._tweens.removeAll();
		super.destroy();
	}

	update() {
		this._tweens.update();
	}

	changeType(toolType) {
		if (this._type === toolType) return;

		this._type = toolType;

		this[_removeItems]();
		this._pageIndex = 0;
		this._arrBtnInfo = this[_findItems](this._type);

		this[_updateNavControls]();

		this[_createItems](this._arrBtnInfo);
	}

	[_removeItems]() {
		for (const menuButton of this._boxSprite.children) {
			menuButton.interactive = false;

			new P3.utils.TWEEN.Tween(menuButton.scale, this.tweens)
			    .to({
			        x: 0, y: 0
			    }, 200)
				.delay(Math.random() * 200)
			    .easing(P3.utils.TWEEN.Easing.Quadratic.In)
			    .onComplete(()=> {
					this._boxSprite.removeChild(menuButton);
			    })
			    .start();
		}
	}

	[_findItems](paletteType) {
		const result = [];

		const PALETTE_PAGES = common.gameConfig.palettePanel.pages;
		const arrPageIndex = this.PALETTE_TYPE_TO_TOOL[paletteType];

		for (const pageIndex of arrPageIndex) {
			result.push([]);
			for (const item of PALETTE_PAGES[pageIndex]) {
				if (item.indexOf('#') === 0)	{
					// Colour.
					result[result.length - 1].push({
						out: 'btns_colour_white',
						over: 'btns_colour_white',
						down: 'btns_colour_white',
						tint: parseInt('0x' + item.substring(1)),
						isShine: true
					});
				} else if (item.indexOf(commonGame.TEXTURE_SPRITE_BASE_ID) !== -1) {
					// Texture.
					const spriteId = commonGame.TEXTURE_BUTTON_BASE_ID + item.substring(item.length - 2);
					result[result.length - 1].push({out: spriteId, over: spriteId, down: spriteId, isShine: true});
				} else
				// Stamp.
					result[result.length - 1].push({out: item, over: item, down: item, isShine: false});
			}
		}

		return result;
	}

	[_createItems](arrBtnInfo) {
		const panelConfig = common.levelConfig.game_scene.palette_panel;
		// Create buttons for the selected page.
		const arrCurPageBtnInfo = this._arrBtnInfo[this._pageIndex];
		let btnIndex = 0;
		let colIndex = 0;
		let rowIndex = 0;
		const arcHeight = 7;
		const rowCount = Math.ceil(arrCurPageBtnInfo.length / this.ITEMS_PER_ROW);
		let startingX = -this._boxSprite.width * 0.5 + (this._boxSprite.width - this[_findItemCount](rowIndex, arrCurPageBtnInfo.length) * 100 + 125) * 0.5;
		let startingY = -this._boxSprite.height * 0.5 + (this._boxSprite.height - (rowCount - 1) * 110) * 0.5;

		startingX += panelConfig.offset.x;
		startingY += panelConfig.offset.y;

		console.log(startingX, startingY, this._boxSprite.width, this._boxSprite.height);

		// TODO: Set starting X and Y according to the number of items for the given row and the number of rows.
		for (const btnInfo of arrCurPageBtnInfo) {
			const btnStatesAux = new P3.ButtonStates(
				P3.assets.texture(btnInfo.out),
				P3.assets.texture(btnInfo.over),
				P3.assets.texture(btnInfo.down));
			const menuButton = new P3.Button(btnStatesAux);
			menuButton.tint = btnInfo.tint != null ? btnInfo.tint : 0xffffff;
			menuButton.anchor = new PIXI.Point(0.5, 0.5);
			//menuButton.x = startingX + colIndex * 135;
			menuButton.x = startingX + colIndex * 95;
			menuButton.y = startingY + rowIndex * 125;
			menuButton.y = startingY + rowIndex * 105;
			menuButton.index = btnIndex;
			menuButton.signals.click.once(this[_onMenuButton_Click], this);
			this._boxSprite.addChild(menuButton);

			// Shine overlay.
			if (btnInfo.isShine) {
				const shineOverlay = new PIXI.Sprite(P3.assets.texture('btns_colour_overlay'));
				shineOverlay.anchor = new PIXI.Point(0.5, 0.5);
				shineOverlay.alpha = 0.4;
				shineOverlay.blendMode = PIXI.BLEND_MODES.ADD;
				menuButton.addChild(shineOverlay);
			}

			++btnIndex;

			++colIndex;
			if (colIndex >= this.ITEMS_PER_ROW) {
				colIndex = 0;
				++rowIndex;

				//startingX = -this._boxSprite.width * 0.5 + ( this._boxSprite.width - this._findItemCount(rowIndex, arrCurPageBtnInfo.length) * 135 + 125 ) * 0.5;
				startingX = -this._boxSprite.width * 0.5 + (this._boxSprite.width - this[_findItemCount](rowIndex, arrCurPageBtnInfo.length) * 100 + 125) * 0.5;
				startingX += panelConfig.offset.x;
			}

			// Animate the button.
			menuButton.scale = new PIXI.Point();
			//menuButton.defaultScale = new PIXI.Point(0.6, 0.6);

			new P3.utils.TWEEN.Tween(menuButton.scale, this.tweens)
			    .to({
			        x: 0.6, y: 0.6
			    }, 0.2)
				.delay(Math.random() * 200)
			    .easing(P3.utils.TWEEN.Easing.Quadratic.Out)
			    .start();
		}
	}

	[_findItemCount](rowIndex, btnCount) {
		let result = 0;

		let remainingBtnCount = btnCount - rowIndex * this.ITEMS_PER_ROW;
		if (remainingBtnCount / this.ITEMS_PER_ROW >= 1) result = this.ITEMS_PER_ROW;
		else result = remainingBtnCount % this.ITEMS_PER_ROW;

		return result;
	}

	[_updateNavControls]() {
		const tween = (btn, alpha, easing)=> {
			new P3.utils.TWEEN.Tween(btn, this.tweens)
				.to({
					alpha: alpha
				}, 250)
				.easing(easing)
				.onStart(()=> {
					btn.interactive = btn.alpha === 0;
				})
				.start();
		};

		if (this._prevBtn.alpha === 0) {
			if (this._pageIndex > 0) {
				tween(this._prevBtn, 1, P3.utils.TWEEN.Easing.Quadratic.Out);
				this._prevBtn.interactive = true;
			}
		} else {
			if (this._pageIndex === 0) {
				tween(this._prevBtn, 0, P3.utils.TWEEN.Easing.Quadratic.In);
				this._prevBtn.interactive = false;
			}
		}

		if (this._nextBtn.alpha === 0) {
			if (this._pageIndex < this._arrBtnInfo.length - 1) {
				tween(this._nextBtn, 1, P3.utils.TWEEN.Easing.Quadratic.Out);
				this._nextBtn.interactive = true;
			}
		} else {
			if (this._pageIndex === this._arrBtnInfo.length - 1) {
				tween(this._nextBtn, 0, P3.utils.TWEEN.Easing.Quadratic.In);
				this._nextBtn.interactive = false;
			}
		}
	}

	[_onPageIndex_Changed]() {
		// Navigation controls.
		this[_updateNavControls]();

		// Menu buttons.
		this[_removeItems]();
		this[_createItems](this._arrBtnInfo);
	}

	[_onNextButton_Click]() {
		if (this._pageIndex < this._arrBtnInfo.length - 1) {
			this._pageIndex += 1;

			this[_onPageIndex_Changed]();
			P3.audio.playSound('sfx_ui_carousel_swipe_00');
		}
	}

	[_onPrevButton_Click]() {
		if (this._pageIndex > 0) {
			this._pageIndex -= 1;

			this[_onPageIndex_Changed]();
			P3.audio.playSound('sfx_ui_carousel_swipe_00');
		}
	}

	[_onMenuButton_Click](btn) {
		switch (this._type) {
			case commonGame.TOOL.PENCIL:
			case commonGame.TOOL.BRUSH:
			case commonGame.TOOL.BUCKET:
			case commonGame.TOOL.INK_POT:
			case commonGame.TOOL.SPRAY: {
				this.signals.onColourChanged.dispatch(
					common.gameConfig.palettePanel.pages[this.PALETTE_TYPE_TO_TOOL[this._type][this._pageIndex]][btn.index],
					this.PALETTE_TYPE_TO_TOOL[this._type][this._pageIndex],
					btn.index);
				break;
			}

			case commonGame.TOOL.STAMP: {
				this.signals.onStampChanged.dispatch(
					common.gameConfig.palettePanel.pages[this.PALETTE_TYPE_TO_TOOL[this._type][this._pageIndex]][btn.index],
					this.PALETTE_TYPE_TO_TOOL[this._type][this._pageIndex],
					btn.index);
				break;
			}
		}

		this.hide();

		// Sfx.
		P3.audio.playSound('sfx_generic_colour_type_change_00');
	}

	get tweens() {
		return this._tweens;
	}
}
